import { MAX_RECORDING_DURATION_MINUTES } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/constants";
import { generateRandomString } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/helpers";
import { useWaveSurferRecorder } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/hooks/useWaveSurferRecorder";
import { useWavesurfer } from "@wavesurfer/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";

import type { VoiceMessageModalProps } from "./types";
import { useBlockEditorWebsocket } from "./useBlockEditorWebsocket";

export interface VoiceMessageRecordingProps extends Required<VoiceMessageModalProps> {
  onClose: () => void;
}

export const useVoiceMessageRecording = ({
  addToLocalInlineAttachments,
  editor,
  onClose,
  range,
}: VoiceMessageRecordingProps) => {
  const recordingId = useMemo(() => generateRandomString(), []);
  const waveformRef = useRef<HTMLDivElement>(null);
  const recordRef = useRef<ReturnType<typeof RecordPlugin.create> | null>(null);
  const cancelledRef = useRef(false);
  const [duration, setDuration] = useState(0);

  const { isBusy, isError, handleBlob, onRetry, setServerError } =
    useWaveSurferRecorder({
      recordingId,
    });

  useBlockEditorWebsocket({
    onClose,
    recordingId,
    setServerError,
    addToLocalInlineAttachments,
    editor,
    range,
  });

  const wsOptions = useMemo(
    () => ({
      container: waveformRef,
      waveColor: "#C8CCD2",
      progressColor: "#191B1F",
      cursorColor: "transparent",
      barWidth: 3,
      barGap: 3,
      barRadius: 4,
      height: 32,
      normalize: true,
    }),
    [],
  );

  const { wavesurfer } = useWavesurfer(wsOptions);

  useEffect(() => {
    if (!wavesurfer) return;

    const record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        scrollingWaveformWindow: 7,
        renderRecordedAudio: false,
      }),
    );

    recordRef.current = record;

    const unsubProgress = record.on("record-progress", (timeMs: number) => {
      setDuration(Math.floor(timeMs / 1000));
    });

    const unsubEnd = record.on("record-end", (blob: Blob) => {
      if (!cancelledRef.current) {
        handleBlob(blob);
      }
    });

    void record.startRecording();

    const timeout = setTimeout(
      () => {
        if (record.isRecording()) {
          record.stopRecording();
        }
      },
      MAX_RECORDING_DURATION_MINUTES * 60 * 1000,
    );

    return () => {
      clearTimeout(timeout);
      cancelledRef.current = true;
      unsubProgress();
      unsubEnd();
      if (record.isRecording()) {
        record.stopRecording();
      }
      record.destroy();
    };
  }, [wavesurfer, handleBlob]);

  const handleClose = useCallback(() => {
    cancelledRef.current = true;
    if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording();
    }
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (isError) {
      onRetry();
    } else if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording();
    }
  }, [isError, onRetry]);

  return {
    duration,
    handleClose,
    handleSubmit,
    isBusy,
    isError,
    waveformRef,
  };
};
